import { PaymentStatusOption, PaymentLifecycleView } from "helpers/paymentLifecycle";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

export interface DepositItem extends PaymentLifecycleView {
  id: number;
  userId: number;
  amountAttempt: number;
  amountPaid: number;
  status: string;
  fee?: number;
  currency: string;
  paymentMethod: string;
  paymentMethodImage?: string | null;
  paymentProviderImage?: string | null;
  depositType?: string | null;
  externalRef?: string | null;
  externalTransactionId?: string | null;
  created?: string | null;
  completed?: string | null;
  completedAt?: string | null;
  processingType?: string;
}

interface PaymentsDepositsState {
  statusesList: PaymentStatusOption[];
  isStatusesFetched: boolean;
  list: DepositItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  selectedItems: number[];
  processingItemId?: number;
}
interface GetListPayload {
  limit: number;
  offset: number;
  paymentMethodIds?: number[];
  paymentProviderIds?: number[];
  canonical_status?: string[];
  currencies?: string[];
  userIds?: number[];
  operationTypes?: string[];
  depositId?: number;
  externalRef?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  completedAtFrom?: string;
  completedAtTo?: string;
  order?: {
    userId?: string;
    amountAttempt?: string;
    id?: string;
    fee?: string;
    currency?: string;
    status?: string;
    depositType?: string;
    paymentMethod?: string;
    created?: string;
    completed?: string;
  };
}
const initialState: PaymentsDepositsState = {
  statusesList: [],
  isStatusesFetched: false,
  list: [],
  isListFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
};

export const getDepositsList = createAsyncThunk(
  "paymentsDeposit/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.paymenDepositsList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getDepositStatusesList = createAsyncThunk(
  "paymentsDeposit/statuses",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentDepositStatuses}?limit=500&offset=0&canonical=true`,
      );
      return result.data.items;
    } catch (error) {
      return [];
    }
  },
);

export const modifyDepositStatus = createAsyncThunk(
  "paymentsDeposit/modifyStatus",
  async (
    { id, statusId }: { id: number; statusId: number },
    { rejectWithValue },
  ) => {
    try {
      const result = await instance.put(
        `${apiRoutes.paymentDepositModifyStatus}/${id}`,
        {
          statusId,
        },
      );

      return result.data;
    } catch (error: any) {
      const data = error?.data || error?.response?.data;

      const apiErrors = data?.errors;

      if (Array.isArray(apiErrors)) {
        return rejectWithValue({
          code: "BusinessError",
          fieldErrors: [],
          globalErrors: apiErrors.map((item: { message: string }) => ({
            code: item.message,
          })),
        });
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const paymentsDepositSlice = createSlice({
  name: "paymentsDeposit",
  initialState,
  reducers: {
    handlePaginate: (
      state: PaymentsDepositsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleSelectItem: (
      state: PaymentsDepositsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: PaymentsDepositsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDepositStatusesList.fulfilled, (state, action) => {
      state.statusesList = action.payload.map((el: any) => {
        return {
          value: el.code,
          statusId: el.statusId,
          label: el.title,
        };
      });
      state.isStatusesFetched = true;
    });
    builder.addCase(getDepositsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(modifyDepositStatus.pending, (state, action) => {
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(modifyDepositStatus.fulfilled, (state, action) => {
      state.processingItemId = undefined;

      const updated = action.payload?.data;

      if (!updated) return;

      const index = state.list.findIndex((el) => el.id === updated.id);

      if (index !== -1) {
        state.list[index].status = updated.status;
        state.list[index].canonical_status = updated.canonical_status;
        state.list[index].allowed_status_actions = updated.allowed_status_actions ?? [];
      }
    });
    builder.addCase(modifyDepositStatus.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

export const { handlePaginate, handleSelectAll, handleSelectItem } =
  paymentsDepositSlice.actions;
